import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'
import type { Feature } from '../feature'

/**
 * ```
 * loadRecords.request
 *   │
 *   ├─ groupBy(entityId)          one stream per entity
 *   │   │
 *   │   ├─ buffer(debounce 100ms) collect rapid-fire dispatches
 *   │   │   │
 *   │   │   │  ──a]──b]──c]──────────▸ [a, b, c]
 *   │   │   │
 *   │   ├─ concatMap               queue batches (no drops)
 *   │   │   │
 *   │   │   ├─ read latest         fresh context & lookup
 *   │   │   │  (combineLatest+first)
 *   │   │   │
 *   │   │   ├─ merge recordIds     dedupe across batch
 *   │   │   │
 *   │   │   ├─ cache hit?  ──yes──▸ cancellation
 *   │   │   │
 *   │   │   └─ api.loadRecords ───▸ success / error
 *   │   │
 *   │   └─ (next queued batch picks up fresh cache)
 *   │
 *   └─ mergeMap                    entities run in parallel
 * ```
 */

const BUFFER_MS = 100

export const loadRecordsOnRequest = store.effects.create(
  (feature: Feature, meFeature: me.Feature) =>
    feature.actions.loadRecords.request.observe().pipe(
      // Group by entityId so each entity gets its own stream
      rxjs.groupBy(action => action.data.entityId),
      rxjs.mergeMap(group$ => {
        const shared$ = group$.pipe(rxjs.share())
        return shared$.pipe(
          // Buffer rapid-fire dispatches until there is a pause
          rxjs.buffer(shared$.pipe(rxjs.debounceTime(BUFFER_MS))),
          rxjs.filter(buffered => buffered.length > 0),
          // concatMap: queue subsequent batches so no requests are lost.
          // The cache check inside will skip records already loaded by
          // a previous batch, making queued batches essentially free.
          rxjs.concatMap(buffered =>
            // Re-read context & lookup at processing time so queued batches
            // see records that were loaded by earlier batches
            rxjs
              .combineLatest([
                meFeature.contexts.selectors.zoneContext.asObservable,
                feature.selectors.lookup.asObservable,
              ])
              .pipe(
                rxjs.first(),
                rxjs.mergeMap(([context, lookup]) => {
                  const lastAction = buffered[buffered.length - 1]
                  if (!context) {
                    // NOTE: Without a zone context we can't build the API request,
                    // so we skip the action entirely.
                    return rxjs.of(store.effects.SKIP)
                  }

                  const entityId = lastAction.data.entityId

                  // Merge all recordIds from every buffered action
                  const allRecordIds = [
                    ...new Set(buffered.flatMap(a => a.data.recordIds ?? [])),
                  ]

                  // Cache check: if every merged recordId is already cached, skip
                  const cached = lookup[entityId]
                  const allCacheFirst = buffered.every(a => a.data.cacheFirst)
                  if (allCacheFirst && cached?.records?.length) {
                    // When no specific recordIds are requested we want ALL
                    // records — only skip if the cache is marked complete
                    // (i.e. a previous unfiltered fetch already loaded
                    // everything). This prevents a partial cache (e.g.
                    // populated by the readonly view with specific
                    // recordIds) from short-circuiting a full load.
                    const allCached = allRecordIds.length
                      ? allRecordIds.every(id =>
                          cached.records?.some(r => r.id === id),
                        )
                      : !!cached.complete

                    if (allCached) {
                      return rxjs.of(
                        feature.actions.loadRecords.cancellation
                          .from(lastAction)
                          .create({ request: lastAction.data }),
                      )
                    }
                  }

                  // Make one API call with the combined recordIds
                  const records$ = feature.api.loadRecords({
                    ...context,
                    entityId,
                    recordIds: allRecordIds,
                  })
                  // TODO: after the first response, everything else should be a
                  // follow up success or follow up error
                  return records$.pipe(
                    // Handles success
                    rxjs.map(records =>
                      feature.actions.loadRecords.success
                        .from(lastAction)
                        .create({
                          request: {
                            ...lastAction.data,
                            recordIds: allRecordIds.length
                              ? allRecordIds
                              : undefined,
                          },
                          response: { records },
                        }),
                    ),
                    // Handles error
                    rxjs.catchError((error: Error | undefined) =>
                      rxjs.of(
                        feature.actions.loadRecords.error
                          .from(lastAction)
                          .create({
                            error,
                            request: lastAction.data,
                          }),
                      ),
                    ),
                  )
                }),
              ),
          ),
        )
      }),
    ),
)
